import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BarChart2, CheckCircle2, ExternalLink, Heart, PlusCircle, X } from "lucide-react";
import { supabase, goUrl } from "@/lib/supabase";
import { VisitedBadge } from "@/components/VisitedBadge";
import { posthog } from "@/lib/posthog";
import { useSession } from "@/hooks/useSession";
import { useProfile } from "@/hooks/useProfile";
import { ListingDetailDrawer, type Listing } from "@/components/ListingDetailDrawer";
import { ComparadorPanel } from "@/components/ComparadorPanel";
import { AppNav } from "@/components/AppNav";

export const Route = createFileRoute("/favoritos")({
  head: () => ({
    meta: [
      { title: "Favoritos — Busca Blindados" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: FavoritosPage,
});

type FavoriteEntry = {
  id: string;
  listing_id: string;
  watchlist_name: string | null;
  price_at_save: number | null;
  created_at: string;
  listing: (Listing & { status: string }) | null;
};

const COMPARADOR_MAX = 4;

function fmtBR(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}

function priceDelta(current: number | null, atSave: number | null) {
  if (current == null || atSave == null) return null;
  return current - atSave;
}

function FavoritosPage() {
  const { session, loading: sessionLoading } = useSession();
  const { isPaid, isFree, loading: profileLoading } = useProfile();
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionLoading || profileLoading) return;
    if (!session) { navigate({ to: "/login" }); return; }
    if (!isPaid && !isFree) { navigate({ to: "/planos" }); return; }
  }, [session, sessionLoading, isPaid, isFree, profileLoading, navigate]);

  const [entries, setEntries] = useState<FavoriteEntry[] | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [comparadorItems, setComparadorItems] = useState<Listing[]>([]);
  const [comparadorOpen, setComparadorOpen] = useState(false);
  const [activeList, setActiveList] = useState<string | null>(null); // null = "Todos"

  const load = () => {
    if (!session || !isPaid) return;
    supabase
      .from("favorites")
      .select(
        "id, listing_id, watchlist_name, price_at_save, created_at, listings(id, source, title, display_title, model_slug, year, km, price, url, status)",
      )
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) { setFetchError("Não foi possível carregar os favoritos."); return; }
        setEntries(
          (data ?? []).map((row) => ({
            id: row.id,
            listing_id: row.listing_id,
            watchlist_name: row.watchlist_name,
            price_at_save: row.price_at_save,
            created_at: row.created_at,
            listing: row.listings as unknown as (Listing & { status: string }) | null,
          })),
        );
      });
  };

  useEffect(() => { load(); }, [session, isPaid]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveToList = async (favoriteId: string, name: string | null) => {
    setEntries(
      (prev) => prev?.map((e) => (e.id === favoriteId ? { ...e, watchlist_name: name } : e)) ?? null,
    );
    await supabase.from("favorites").update({ watchlist_name: name }).eq("id", favoriteId);
  };

  const removeFavorite = async (entry: FavoriteEntry) => {
    setEntries((prev) => prev?.filter((e) => e.id !== entry.id) ?? null);
    setComparadorItems((prev) => prev.filter((c) => c.id !== entry.listing_id));
    const { error } = await supabase.from("favorites").delete().eq("id", entry.id);
    if (error) load();
  };

  const toggleComparador = (listing: Listing) => {
    setComparadorItems((prev) => {
      if (prev.some((l) => l.id === listing.id)) return prev.filter((l) => l.id !== listing.id);
      if (prev.length >= COMPARADOR_MAX) return prev;
      return [...prev, listing];
    });
  };

  if (sessionLoading || profileLoading) return null;

  if (isFree) {
    const mockLists = [
      {
        name: "SUVs Premium",
        cars: [
          { title: "Toyota SW4 Diamond 2.8 TDI 4x4 7L", year: 2023, km: 18500, price: 379900, source: "r1blindados.com.br", saved: true },
          { title: "Volvo XC90 B6 AWD Inscription", year: 2022, km: 31200, price: 498000, source: "bssblindagens.com.br", saved: true },
        ],
      },
      {
        name: "Para comprar",
        cars: [
          { title: "Jeep Compass Limited T270 AT9", year: 2023, km: 22400, price: 219900, source: "nextmotors.com.br", saved: false },
        ],
      },
    ];
    return (
      <div className="min-h-screen text-foreground">
        <AppNav />
        <main className="mx-auto max-w-6xl px-6 pb-32 pt-10">
          <h1 className="font-display text-3xl text-foreground sm:text-4xl">Favoritos</h1>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed">
            Salve anúncios diretamente do radar ou da busca e organize em listas com nomes que fazem sentido pra você — "SUVs Premium", "Para comprar", o que quiser. Tudo fica salvo na sua conta.
          </p>

          <div className="relative mt-8 pointer-events-none select-none">
            <div className="space-y-6">
              {mockLists.map((list, li) => (
                <div key={li}>
                  <p className="mb-2 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                    {list.name}
                  </p>
                  <div className="space-y-2">
                    {list.cars.map((car, ci) => (
                      <div key={ci} className="surface-card rounded-2xl p-4 flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-foreground truncate">{car.title}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">{car.source} · {car.year} · {car.km.toLocaleString("pt-BR")} km</p>
                          <p className="mt-2 text-sm font-medium text-foreground">R$ {car.price.toLocaleString("pt-BR")}</p>
                        </div>
                        <Heart className={`mt-0.5 h-5 w-5 shrink-0 ${car.saved ? "fill-primary text-primary" : "text-white/30"}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background to-transparent" />
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm font-medium text-foreground">Disponível no Plano Compra e Plano Monitor</p>
            <p className="mt-1 text-sm text-muted-foreground">Salve quantos anúncios quiser e organize em múltiplas listas.</p>
            <Link
              to="/planos"
              className="btn-primary mt-5 inline-flex items-center gap-2 rounded-full px-8 py-3 text-base font-medium"
            >
              Ver planos →
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (!session || !isPaid) return null;

  // Unique named watchlists (excluding null / default)
  const watchlistNames = [
    ...new Set(
      (entries ?? [])
        .map((e) => e.watchlist_name)
        .filter((n): n is string => n !== null),
    ),
  ];

  // Filter entries by active tab; keep gone ones visible
  const visible = (entries ?? []).filter(
    (e) => activeList === null || e.watchlist_name === activeList,
  );

  const activeCount = visible.filter((e) => e.listing?.status === "active").length;
  const goneCount = visible.filter((e) => e.listing?.status === "gone").length;

  return (
    <div className="min-h-screen text-foreground">
      <AppNav />

      <main className="mx-auto max-w-6xl px-6 pb-32 pt-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-foreground sm:text-4xl">Favoritos</h1>
            {entries !== null && (
              <p className="mt-2 text-sm text-muted-foreground">
                {activeCount} ativo{activeCount !== 1 ? "s" : ""}
                {goneCount > 0 && (
                  <span className="ml-2 text-muted-foreground/60">· {goneCount} ficaram indisponíveis</span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Watchlist tabs — Monitor only */}
        {entries !== null && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              onClick={() => setActiveList(null)}
              className={`rounded-full border px-4 py-1.5 text-xs transition ${
                activeList === null
                  ? "border-primary/40 bg-primary/10 text-primary"
                  : "dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] text-foreground/70 dark:hover:bg-white/[0.06] hover:bg-black/[0.06]"
              }`}
            >
              Todos
            </button>
            {watchlistNames.map((name) => (
              <button
                key={name}
                onClick={() => setActiveList(name)}
                className={`rounded-full border px-4 py-1.5 text-xs transition ${
                  activeList === name
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] text-foreground/70 dark:hover:bg-white/[0.06] hover:bg-black/[0.06]"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {fetchError && (
          <p className="mt-6 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {fetchError}
          </p>
        )}

        {entries === null && !fetchError && (
          <div className="surface-card mt-8 rounded-2xl p-6 text-sm text-muted-foreground">
            Carregando...
          </div>
        )}

        {entries?.length === 0 && (
          <div className="surface-card mt-8 rounded-2xl p-10 text-center">
            <p className="font-display text-lg text-foreground">Nenhum favorito ainda</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Salve anúncios tocando no ♡ dentro de qualquer anúncio do radar.
            </p>
          </div>
        )}

        {visible.length > 0 && (
          <>
            {/* Mobile card list */}
            <div className="mt-8 space-y-3 md:hidden">
              {visible.map((entry) => {
                const l = entry.listing;
                if (!l) return null;
                const gone = l.status === "gone";
                const delta = priceDelta(l.price, entry.price_at_save);
                const inComparador = comparadorItems.some((c) => c.id === l.id);
                return (
                  <div
                    key={entry.id}
                    onClick={gone ? undefined : () => { posthog.capture("listing_clicked", { context: "favoritos", source: l.source, price: l.price, year: l.year, model_slug: l.model_slug }); setSelectedListing(l); }}
                    className={`surface-card rounded-2xl p-4 transition ${
                      gone ? "opacity-50" : "cursor-pointer dark:hover:bg-white/[0.03] hover:bg-black/[0.03]"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="min-w-0 truncate font-medium text-foreground">{l.title}</p>
                          <VisitedBadge id={l.id} />
                        </div>
                        {gone && (
                          <span className="mt-0.5 inline-block rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-amber-400">
                            Indisponível
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {l.source}{l.year ? ` · ${l.year}` : ""}
                    </p>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="flex flex-wrap gap-3 text-sm">
                        {l.price != null && (
                          <span className="font-medium text-foreground">R$ {fmtBR(l.price)}</span>
                        )}
                        {delta != null && delta !== 0 && (
                          <span className={`text-xs ${delta < 0 ? "text-emerald-400" : "text-amber-400"}`}>
                            {delta < 0 ? "↓" : "↑"} R$ {fmtBR(Math.abs(delta))}
                          </span>
                        )}
                        {l.km != null && (
                          <span className="text-muted-foreground">{fmtBR(l.km)} km</span>
                        )}
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <FavoriteActions
                          l={l}
                          gone={gone}
                          inComparador={inComparador}
                          onToggleComparador={toggleComparador}
                          onRemove={() => removeFavorite(entry)}
                        />
                      </div>
                    </div>
                    {(
                      <div className="mt-3 border-t dark:border-white/5 border-black/5 pt-2.5" onClick={(e) => e.stopPropagation()}>
                        <WatchlistAssigner
                          current={entry.watchlist_name}
                          options={watchlistNames}
                          onChange={(name) => moveToList(entry.id, name)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="surface-card mt-8 hidden overflow-hidden rounded-3xl md:block">
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-sm">
                  <thead>
                    <tr className="border-b dark:border-white/10 border-black/10 text-left text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                      <th className="w-48 px-5 py-3.5">SITE</th>
                      <th className="px-5 py-3.5">CARRO</th>
                      <th className="w-20 px-5 py-3.5 text-right">ANO</th>
                      <th className="w-28 px-5 py-3.5 text-right">KM</th>
                      <th className="w-32 px-5 py-3.5 text-right">PREÇO</th>
                      {<th className="w-28 px-5 py-3.5">LISTA</th>}
                      <th className="w-28 px-3 py-3.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((entry) => {
                      const l = entry.listing;
                      if (!l) return null;
                      const gone = l.status === "gone";
                      const inComparador = comparadorItems.some((c) => c.id === l.id);
                      return (
                        <tr
                          key={entry.id}
                          onClick={gone ? undefined : () => { posthog.capture("listing_clicked", { context: "favoritos", source: l.source, price: l.price, year: l.year, model_slug: l.model_slug }); setSelectedListing(l); }}
                          className={`border-b dark:border-white/5 border-black/5 text-foreground/90 transition ${
                            gone
                              ? "opacity-50"
                              : "cursor-pointer dark:hover:bg-white/[0.03] hover:bg-black/[0.03]"
                          }`}
                        >
                          <td className="px-5 py-3.5 text-muted-foreground">{l.source}</td>
                          <td className="px-5 py-3.5 font-medium">
                            <span>{l.title}</span>
                            <VisitedBadge id={l.id} className="ml-2 align-middle" />
                            {gone && (
                              <span className="ml-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-amber-400">
                                Indisponível
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-right">{l.year ?? "—"}</td>
                          <td className="px-5 py-3.5 text-right">
                            {l.km != null ? `${fmtBR(l.km)} km` : "—"}
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            {l.price != null ? `R$ ${fmtBR(l.price)}` : "—"}
                          </td>
                          {(
                            <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                              <WatchlistAssigner
                                current={entry.watchlist_name}
                                options={watchlistNames}
                                onChange={(name) => moveToList(entry.id, name)}
                              />
                            </td>
                          )}
                          <td className="pl-3 pr-1 py-3.5" onClick={(e) => e.stopPropagation()}>
                            <FavoriteActions
                              l={l}
                              gone={gone}
                              inComparador={inComparador}
                              onToggleComparador={toggleComparador}
                              onRemove={() => removeFavorite(entry)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      {comparadorItems.length >= 2 && !comparadorOpen && (
        <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2">
          <div className="btn-primary inline-flex items-stretch overflow-hidden rounded-full shadow-lg">
            <button
              onClick={() => { posthog.capture("comparador_opened", { context: "favoritos", listing_count: comparadorItems.length }); setComparadorOpen(true); }}
              className="inline-flex items-center gap-2 whitespace-nowrap px-5 py-2.5 text-sm font-medium"
            >
              <BarChart2 className="h-4 w-4" />
              Comparar {comparadorItems.length}
            </button>
            <div className="w-px self-stretch bg-white/25 my-1.5" />
            <button
              type="button"
              onClick={() => setComparadorItems([])}
              aria-label="Limpar seleção do comparador"
              className="inline-flex items-center px-3 py-2.5 transition hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {selectedListing && session && (
        <ListingDetailDrawer
          listing={selectedListing}
          session={session}
          isInComparador={comparadorItems.some((c) => c.id === selectedListing.id)}
          onClose={() => { setSelectedListing(null); load(); }}
          onToggleComparador={(l) => { toggleComparador(l); }}
        />
      )}

      {comparadorOpen && session && (
        <ComparadorPanel
          listings={comparadorItems}
          session={session}
          onRemove={(id) => {
            setComparadorItems((prev) => prev.filter((l) => l.id !== id));
            if (comparadorItems.length <= 2) setComparadorOpen(false);
          }}
          onClose={() => setComparadorOpen(false)}
        />
      )}
    </div>
  );
}

function FavoriteActions({
  l,
  gone,
  inComparador,
  onToggleComparador,
  onRemove,
}: {
  l: Listing;
  gone: boolean;
  inComparador: boolean;
  onToggleComparador: (l: Listing) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-0.5">
      {!gone && (
        <button
          type="button"
          onClick={() => onToggleComparador(l)}
          title={inComparador ? "Remover do comparador" : "Adicionar ao comparador"}
          className="rounded-full p-1 transition"
        >
          {inComparador
            ? <CheckCircle2 className="h-5 w-5 text-primary" />
            : <PlusCircle className="h-5 w-5 text-muted-foreground/30 hover:text-muted-foreground" />}
        </button>
      )}
      <button
        type="button"
        onClick={onRemove}
        title="Remover dos favoritos"
        className="rounded-full p-1 transition"
      >
        <Heart className="h-5 w-5 fill-primary text-primary" />
      </button>
      {!gone && (
        <a
          href={goUrl(l.id, "table")}
          target="_blank"
          rel="nofollow noopener noreferrer"
          title="Ver anúncio"
          onClick={() => posthog.capture("listing_external_clicked", { listing_id: l.id, context: "favoritos" })}
          className="rounded-full p-1 text-muted-foreground/30 transition hover:text-muted-foreground"
        >
          <ExternalLink className="h-5 w-5" />
        </a>
      )}
    </div>
  );
}

function WatchlistAssigner({
  current,
  options,
  onChange,
}: {
  current: string | null;
  options: string[];
  onChange: (name: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dropPos, setDropPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const all = current && !options.includes(current) ? [current, ...options] : options;

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        !btnRef.current?.contains(e.target as Node) &&
        !dropRef.current?.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div>
      <button
        ref={btnRef}
        onClick={handleToggle}
        className="whitespace-nowrap text-xs text-muted-foreground transition hover:text-foreground"
      >
        {current ?? "Sem lista"} ▾
      </button>
      {open && createPortal(
        <div
          ref={dropRef}
          className="fixed z-50 min-w-[160px] overflow-hidden rounded-lg border dark:border-white/10 border-black/10 bg-popover shadow-lg"
          style={{ top: dropPos.top, left: dropPos.left }}
        >
          <button
            onMouseDown={() => { onChange(null); setOpen(false); }}
            className={`flex w-full px-3 py-2 text-left text-xs transition dark:hover:bg-white/5 hover:bg-black/5 ${
              !current ? "text-primary" : "text-muted-foreground"
            }`}
          >
            Sem lista
          </button>
          {all.map((name) => (
            <button
              key={name}
              onMouseDown={() => { onChange(name); setOpen(false); }}
              className={`flex w-full px-3 py-2 text-left text-xs transition dark:hover:bg-white/5 hover:bg-black/5 ${
                current === name ? "text-primary" : "text-foreground"
              }`}
            >
              {name}
            </button>
          ))}
          <div className="border-t dark:border-white/5 border-black/5 px-2 py-1.5">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newName.trim()) { onChange(newName.trim()); setOpen(false); setNewName(""); }
              }}
            >
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nova lista…"
                className="w-full rounded border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-2 py-1 text-xs text-foreground outline-none focus:border-primary/60"
              />
            </form>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
